<template>
  <div class="tinymce-master-wrapper" :class="{'inline': inline, 'is-focused': is_focused}">
    <div class="fixed-toolbar-container" :class="'fixed-toolbar-' + tinymceKey"></div>
    <div class="block-wrapper" v-if="!inline">
      <textarea class="block-textarea" :class="{'show-border': show_tinymce}" :id="'block-textarea-' + tinymceKey" v-tinymce></textarea>
      <span class="placeholder muted" v-if="showPlaceholderText">{{placeholderText}}</span>
    </div>
    <div class="inline-wrapper" :class="[alignmentClass, fontSizeClass]" v-if="inline">
      <div class="inline-textarea" :class="{'show-border': show_tinymce}" :id="'inline-textarea-' + tinymceKey" :aria-label="ariaLabelText" v-tinymce></div>
      <span class="placeholder" v-if="showPlaceholderText" :class="{'is_hovered': hoverLabel && (input_length || is_focused), 'hoverable': hoverLabel, 'muted': !interactionMeta.darkMode}">{{placeholderText}}</span>
    </div>
    <div class="v-text-field__details pt-1" >
    	<div class="v-messages theme--light error--text">
    		<div class="v-messages__wrapper">
    			<div v-for="err in tinymceErrors" v-if='err.active' class="v-messages__message py-1">{{err.text}}</div>
    		</div>
    	</div>
      <v-layout align-center justify-end row fill-height>
        <template v-if="interaction.interactionType === 'discussion_q' && (!['reply', 'edit'].includes(mode))">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                :disabled="!discussionInteractionHasCommentReplies"
                :aria-label="(!showReplies) ? 'Expand all reply threads' : 'Collapse all reply threads'"
                class="ma-0"
                icon
                width="24"
                height="24"
                @click="$emit('expand-all-reply-threads', (!showReplies))">
                <v-icon color="grey darken-1">{{!showReplies ? 'unfold_more' : 'unfold_less' }}</v-icon>         
              </v-btn>
            </template>
            <span>{{!showReplies ? 'Expand all reply threads' : 'Collapse all reply threads'}}</span>
          </v-tooltip>
          <div class="items-partition"></div>
        </template>
        <template v-if="!mobile && showKeyboardShortcutIcon && (!['reply', 'edit'].includes(mode))">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" aria-label="Keyboard shortcuts information" ref="keyboardInfo" class="ma-0" icon width="24" height="24" @click="keyboardDialog = true">
                <v-icon color="grey darken-1">keyboard</v-icon>         
              </v-btn>
            </template>
            <span>Keyboard shortcuts</span>
          </v-tooltip>
            <div class="items-partition"></div>
        </template>
        <div class="v-counter" :class="interactionMeta.darkMode ? 'dark' : 'theme--light'">{{charCount}}</div>
      </v-layout>
    </div>
    <div class="audio-recorder text-xs-center" v-show="recordAudioMode" ref="audioRecorder">
      <PlaypositAudioRecorder
        v-if="recordAudioMode"
        @success="handleAudioRecorderSuccess"
        @error="handleAudioRecorderError"
        :key="recordAudioMode ? 1 : 0"
        :server-url="serverURL"
        :token="user.token"/>
    </div>
    <div v-if="attachments && attachments.length">
      <v-subheader class="pl-2">Attachments</v-subheader>
      <v-item-group multiple>
        <v-item
          v-for="attachment in attachments"
          :key="attachment.key">
          <v-hover>
            <v-chip
              slot-scope="{ hover }"
              :color="hover ? 'grey lighten-1' : 'grey lighten-2'"
              class="leading-button-chip no-focus-chip pointer"
              close
              :aria-label="`Press Enter to download the following attachment: ${attachment.name}. Press Shift+Enter to delete it.`"
              @input="deleteAttachment(attachment)"
              @click="attachment.status === 'ready' ? downloadAttachment(attachment) : null"
              @keyup.enter.exact="attachment.status === 'ready' ? downloadAttachment(attachment) : null"
              @keyup.space="attachment.status === 'ready' ? downloadAttachment(attachment) : null"
              @keyup.shift.enter="deleteAttachment(attachment)">
              <v-progress-circular
                v-if="attachment.status === 'processing'"
                class="mr-2 leading"
                indeterminate
                :size="20"
                :width="2">
              </v-progress-circular>
              <v-icon
                v-else
                class="mr-2 leading"
                :color="hover ? null : 'grey darken-1'"
                size="20">
                description
              </v-icon>
              <span class="grey--text text--darken-3">{{attachment.name}}</span>
              <span
                v-if="attachment.status === 'ready'"
                role="alert"
                class="screen-reader-tag">
                The following attachment has been successfully uploaded: {{ attachment.name }}.
              </span>
            </v-chip>
          </v-hover>
        </v-item>
      </v-item-group>
    </div>
    <!-- Video record dialog -->
    <v-dialog
      content-class="full-height white overflow-y-hidden"
      v-model="recordVideoMode"
      persistent>
      <PlaypositVideoTranscoder
        v-if="recordVideoMode"
        :api-url="serverURL"
        :token="user.token"
        :teacher-id="rootTeacher.id"
        :bulb-attempt-id="bulbAttempt.id"
        :student-id="userIsTrueTeacher ? null : user.id"
        :discussion-attempt-interaction-id="userIsTrueTeacher && interaction.interactionType === 'discussion_q' ? interactionId : null"
        starting-key="upload"
        @success="handleVideoTranscoderSuccess"
        @error="handleVideoTranscoderError"
        @exit="handleVideoTranscoderExit"
      />
    </v-dialog>
    <!-- Keyboard info dialog -->
    <v-dialog
      v-model="keyboardDialog"
      width="777">
      <v-card>
        <v-card-title class="lighten-1 font-weight-medium">
          <v-layout align-center justify-space-between row fill-height>
            <h2>Keyboard Shortcuts</h2>
            <v-btn icon class="mr-1" tabindex="0" @click="keyboardDialog = false">
              <v-icon>
                close
              </v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div tabindex="-1">
          <ul style="list-style-type: none">
            <li class="subheading"><span class="font-weight-bold">{{!tinymceEnvironment.mac ? `Shift + Alt + 0` : `Ctrl + Option + 0`}}</span> Open this keyboard shortcuts dialog</li>
            <li class="subheading"><span class="font-weight-bold">{{!tinymceEnvironment.mac ? `Alt + 0` : `Option + 0`}}</span> Open rich text editor help dialog</li>
            <li class="subheading"><span class="font-weight-bold">{{!tinymceEnvironment.mac ? `Alt + F9` : `fn + Option + F9`}}</span> Go to the editor's menubar</li>
            <li class="subheading"><span class="font-weight-bold">{{!tinymceEnvironment.mac ? `Alt + F10` : `fn + Option + F10`}}</span> Go to the editor's toolbar</li>
            <li class="subheading"><span class="font-weight-bold">Esc</span> Close a menu or dialog. Also returns you to the editor area</li>
            <li class="subheading"><span class="font-weight-bold">Tab / Arrows</span> Navigate through the menu or toolbar</li>
          </ul>
          <br>
          <p class="subheading">Other editor shortcuts may be found at <a href="https://www.tiny.cloud/docs-4x/advanced/keyboard-shortcuts/#editorkeyboardshortcuts" target="_blank">here</a>.</p>
          </div>
        </v-card-text>
        <v-divider></v-divider>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* global tinymce */
/* global editor */
/* global navigator */
/* global StereoAudioRecorder */
/* global _ */
/* global dataLayer */

// Pluings / Libraries:
import Vue from 'vue';
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';
import axios from 'axios';

// Services
import EventBus from '@/buses/EventBus';
import PlayerService from '@/services/PlayerService';

// Components
import PlaypositAudioRecorder from '@components/audioRecorder';
window.node_api_prefix = PlayerService.url;

const watchEditor = _.throttle((event, v, ed) => {
  let content = ed.getContent();
  if (['<p><br data-mce-bogus="1"></p>', '<p><br></p>', ''].includes(content)) {
    ed.setContent('');
    v.$emit('tinymce-input', '');
    v.input_length = 0;
    v.interactionMeta.isAnswerEmpty = true;
  } else {
    v.$emit('tinymce-input', content);
    v.input_length = content.length;
    v.interactionMeta.isAnswerEmpty = content.length === 0;
  }
  v.charCount = ed.plugins.charcount.getCount(null, true); // count whitespace
}, 250, {
  leading: true,
  trailing: true,
});

function scrollToElement (selector) {
  var topPos = document.querySelector(selector).offsetTop;
  if (topPos === 0) {
    setTimeout(function() {
      scrollToElement(selector);
    }, 25);
  } else {
    document.querySelector('html').scrollTop = topPos - 10;
  }
}

/**
 * Creating the Vue-directive that wraps our Player's tinymce instance.
 * 
 * @see https://www.tiny.cloud/docs-4x/
 * 
 * Side-note: Would love to spend the time upgrading our tinymce vesrion to the latest, or at least >= 5.0.
 * There are a lot of added, new features that we could greatly benefit from.
 * 
 * Reference: https://playposit.atlassian.net/browse/PP-727
 */
Vue.directive('tinymce', {
  bind(el, binding, vnode) {
    const v = vnode.context;
    const ua = window.navigator.userAgent;
    const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    const webkit = !!ua.match(/WebKit/i);
    const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
    v.$nextTick(() => {
      const init = function() {
        let defaultMenuConfig = {
          edit: {
            title: 'Edit',
            items: 'undo redo  | cut copy paste selectall | searchreplace',
          },
          insert: {
            title: 'Insert',
            items: 'image link charmap eqneditor code',
          },
          format: {
            title: 'Format',
            items: 'underline bold italic | fontsizeselect formats | removeformat',
          },
          table: {
            title: 'Table',
            items: 'inserttable tableprops deletetable | cell row column',
          },
        };
        let defaultToolbarConfig = 
          iOSSafari
          ? `emoticons image eqneditor bold italic numlist bullist charmap showmenu`
          : `emoticons video wami attachFile image eqneditor bold italic numlist bullist charmap showmenu`;
        if (['discussion_q'].includes(v.interaction.interactionType)) {
          defaultToolbarConfig = `emoticons video wami attachFile numlist bullist table showmenu`;
          delete defaultMenuConfig.table;
        }
        tinymce.remove('#' + el.id);
        tinymce.init({
          custom: {
            node_api_prefix: PlayerService.url,
          },
          target: el,
          theme: 'modern',
          format: 'html',
          skin_url: 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.7.6/skins/lightgray',
          min_height: 100,
          min_width: 250,
          body_class: 'inline-textarea',
          resize: true,
          entity_encoding: 'raw',
          relative_urls: false,
          keep_styles: false,
          forced_root_block : (v.inline) ? '' : 'p',
          inline: v.inline,
          fixed_toolbar_container: '.fixed-toolbar-' + v.tinymceKey,
          inline_boundaries_selector: 'a[href],input,span.playposit-audio-upload',
          link_assume_external_targets: true,
          default_link_target: '_blank',
          extended_valid_elements: 'audio[controlslist|controls]',
          menu: defaultMenuConfig,
          external_plugins: {
            'charcount' : PlayerService.url + 'hosted/charcount/plugin.js',
            'emoticons' : PlayerService.url + 'hosted/emoticons/plugin.js',
            'eqneditor' : PlayerService.url + 'hosted/eqneditor/plugin.min.js',
            'help'      : PlayerService.url + 'hosted/help/plugin.js',
          },
          plugins: [
            "autoresize eqneditor advlist autolink lists link image charmap",
            "searchreplace visualblocks code",
            "table contextmenu paste autoresize imagetools help",
          ],
          toolbar: defaultToolbarConfig,
          style_formats: [
              {
                title: 'Headers',
                items:[
                  {
                    title: 'Header 1',
                    format: 'h1'
                  },
                  {
                    title: 'Header 2',
                    format: 'h2'
                  },
                  {
                    title: 'Header 3',
                    format: 'h3'
                  }
                ]
              },
              {
                title: 'Inline',
                items: [
                  {
                    title: 'Strikethrough',
                    icon: 'strikethrough',
                    format: 'strikethrough'
                  },
                  {
                    title: 'Superscript',
                    icon: 'superscript',
                    format: 'superscript'
                  },
                  {
                    title: 'Subscript',
                    icon: 'subscript',
                    format: 'subscript'
                  },
                  {
                    title: 'Code',
                    icon: 'code',
                    format: 'code'
                  }
                ]
              },
              {
                title: 'Alignment',
                items: [
                  {
                    title: 'Left',
                    icon: 'alignleft',
                    format: 'alignleft'
                  },
                  {
                    title: 'Center',
                    icon: 'aligncenter',
                    format: 'aligncenter'
                  },
                  {
                    title: 'Right',
                    icon: 'alignright',
                    format: 'alignright'
                  },
                  {
                    title: 'Justify',
                    icon: 'alignjustify',
                    format: 'alignjustify'
                  }
                ]
              }
          ],
          theme_advanced_font_sizes: "10px,12px,13px,14px,16px,18px,20px",
          font_size_style_values: "12px,13px,14px,16px,18px,20px",
          autosave_ask_before_unload: false,
          statusbar: false,
          images_upload_url: `${PlayerService.url}upload/image`,
          images_upload_handler(blobInfo, success, failure) {
            if (!v.user.readOnly) {
              setTimeout(() => {
                const fd = new FormData();
                fd.append('file', blobInfo.blob(), blobInfo.filename());
                PlayerService.uploadImage(fd)
                .then(response => {
                  success(response.data.imageUrl);
                })
                .catch(err => {
                  failure(err.msg);
                });
              }, 2000);
            }
            else {
              failure("User cannot upload image in read only mode.")
            }
          },
          image_class_list: [
            {
              title: 'Responsive',
              value: 'responsive'
            }
          ],
          setup: (ed) => {
            ed.on('init', function() {
              const tinymceEnv = tinymce.util.Tools.resolve('tinymce.Env');
              const [meta, access] = [(tinymceEnv.mac ? '\u2318' : 'Ctrl'), (tinymceEnv.mac ? 'Ctrl + Alt' : 'Shift + Alt')];
              ed.shortcuts.add(`${access}+0`, 'Open PlayPosit custom help dialog', function() {
                v.$refs.keyboardInfo.$el.click();
              });
            });
            if (v.inline) {
              ed.addButton('showmenu', {
                title: 'Show menu',
                icon: 'more_vert',
                onclick: function() {
                  var mainContainer = document.querySelector(`.fixed-toolbar-${v.tinymceKey} .mce-tinymce-inline.mce-container`);
                  var isVisible = mainContainer.classList.contains('extended');
                  if (v.inline) {
                    if (!isVisible) {
                      mainContainer.classList.add('extended');
                    } else {
                      mainContainer.classList.remove('extended');
                    }
                  }
                }
              });
            }
            if (!v.disabledButtons || !v.disabledButtons.includes('wami')) {
              ed.addButton('wami', {
                  title : 'Audio',
                  image: require('@/assets/mic.png'),
                  onclick : function() {
                    v.recordVideoMode = false;
                    v.recordAudioMode = !v.recordAudioMode;
                    if (v.recordAudioMode) {
                      scrollToElement('.audio-recorder');
                    }
                  }
              });
            }
            if (_.get(v, 'interaction.config.allow_learner_attachments', false) && v.bulbAttempt.id !== 0) {
              ed.addButton('attachFile', {
                title: 'Attach a file',
                image: 'https://cdn.playposit.com/image-uploads/image-07-01-2021-14-01-33--attachment.png',
                onclick: () => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.addEventListener('change', v.handleFileAttachment);
                  input.click();
                }
              });
            }
            const hostingTeacherIdentity = _.get(v.rootTeacher, 'teacherIdentities', []).find(ti => _.get(ti, 'identityType') === 'hosting');
            const hasHosting = (
              (
                _.has(v.rootTeacher, 'school.hosting')
                && (
                  [1, 3, 4, 5].includes(v.rootTeacher.school.hosting)
                  && !_.get(v, 'rootTeacher.school.config.permissions', []).includes('hasLimitedVideoUploadAccess')
                )
              )
              || (
                _.get(v.rootTeacher, 'featureLists', []).length
                && parseInt(_.get(hostingTeacherIdentity, 'identityId', 0), 10)
              )
            );
            if (
              hasHosting
              && v.enableVideoUpload
              && !v.isProquestBulb
              && (
                !v.disabledButtons 
                || !v.disabledButtons.includes('video')
              )
            ) {
              // must have hosting and enableUpload not turned off in config and not have disabledButtons or not explicitly disable video
              ed.addButton('video', {
              title : 'Video',
              image: require('@/assets/videocam.png'),
              onclick : function() {
                if (
                  v.queryParams.get('type') !== 'share'
                  || (
                    v.queryParams.get('section_id')
                    && !v.user.isTeacherLmbAttempt
                  )
                ) {
                  v.recordAudioMode = false;
                  v.recordVideoMode = true;
                } else {
                  v.setSnackbar({
                    text: 'Disabled in preview mode.',
                    color: 'warning'
                  });
                }
              }
            });
            }
            if (v.existingText) {
              ed.on('LoadContent', (e) => {
                ed.setContent(v.existingText)
              });
            }
            ed.on('change', (e) => watchEditor(e, v, ed));
            ed.on('keyUp', (e) => watchEditor(e, v, ed));
            ed.on('focus', (e) => {
              if (
                v.tinymceKey === 'notes'
                && v.isDebugFriendlyBulbInBarbriApp
              ) {
                v.lockSidebarStyle();
              }
              document.querySelector('.fixed-toolbar-' + v.tinymceKey).classList.add('toolbar-open');
              v.is_focused = true;
              watchEditor(e, v, ed);
            });
            ed.on('blur', (e) => {
              v.is_focused = false;
              document.querySelector('.fixed-toolbar-' + v.tinymceKey).classList.remove('toolbar-open');
              if (
                v.tinymceKey === 'notes'
                && v.isDebugFriendlyBulbInBarbriApp
              ) {
                setTimeout(() => {
                  v.lockSidebarStyle(false);
                }, 1000);
              }
            });
            EventBus.$on('setTinymce', (obj) => {
              if (obj.key !== v.tinymceKey) return;
              ed.setContent(obj.text);
              v.$emit('tinymce-input', obj.text);
              v.input_length = obj.text.length;
            });
            EventBus.$on('applyExistingText', (key) => {
              if (key !== v.tinymceKey) return;
              ed.setContent(v.existingText);
              v.$emit('tinymce-input', v.existingText);
              v.input_length = v.existingText.length || 0;
              v.charCount = ed.plugins.charcount?.getCount(null, true); // count whitespace
            });
            EventBus.$on('focusTinymce', (key) => {
              if (key !== v.tinymceKey) return;
              ed.focus();
            });
            EventBus.$on('refreshTinymce', (text) => {
              tinymce.execCommand('mceRepaint');
            });
            v.editor = ed;
            v.show_tinymce = true;
          }
        });
      }
      init();
    });
  }
});

export default {
  name: 'interaction-tinymce',
  components: {
    tinymce: Vue.directive('tinymce'),
    PlaypositAudioRecorder,
  },
  props: {
    alignmentClass: null,
    ariaLabel: null,
    disabledButtons: null,
    errorText: null,
    existingText: null,
    fontSizeClass: null,
    hoverLabel: null,
    inline: null,
    inputFontSize: null,
    interactionId: null,
    placeholderText: null,
    attachments: {
      type: Array,
      default: () => ([]),
    },
    tinymceKey: null,
    watchNodes: null,
    enableUpload: null,
    mode: {
      type: String,
      validator: modeType => {
        return ['edit', 'reply', 'comment'].includes(modeType);
      }
    },
    showReplies: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      charCount: 0,
      is_focused: false,
      show_tinymce: false,
      input_length: 0,
      keyboardDialog: false,
      recordAudioMode: false,
      recordVideoMode: false,
      mediaUpload: true,
      recordingEnabled: true,
      isRecording: false,
      recordingDuration: 0,
      recordingTime: {
        start: 0,
        current: 0
      },
      recordingTimeInterval: false,
      recorder: {},
      rootTeacher: null,
      tinymceErrors: [],
      audio: null,
      recordedBlob: null,
      serverURL: null,
    }
  },
  computed: {
    ...mapState('helpers', [
      'fireOnceUponEventEnd',
    ]),
    ...mapState('interactions', [
      'discussionInteractionHasCommentReplies',
    ]),
    ...mapGetters('player', [
      'bulb',
      'bulbAttempt',
      'mobile',
      'queryParams',
      'user',
      'isDebugFriendlyBulbInBarbriApp',
      'isProquestBulb',
    ]),
    ariaLabelText() {
      return this.ariaLabel || 'Input text here';
    },
    enableVideoUpload() {
      if (this.enableUpload === false) return false;
      return true;
    },
    interactionMeta() {
      if (!this.interactionId) return  {isRecording: null, isProcessingData: null};
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {isRecording: null, isProcessingData: null};
    },
    interaction() {
      if (!this.interactionId) return  {};
      return this.$store.getters['interactions/interactions'].find(interaction => interaction.id === this.interactionId) || {}; 
    },
    imageUploadInput() {
      return document.querySelector('#tinymce-file-upload');
    },
    audioDuration() {
      return this.$el.querySelector('audio').duration
    },
    userIsTrueTeacher() {
      return (
        _.get(this, 'user.userType') === 'teacher'
        && !_.get(this, 'user.isTeacherLmbAttempt')
      );
    },
    isUploadAudioDisabled() {
      if (this.interactionMeta.isProcessingData) {
        return true;
      } else {
        if (this.audio === null || this.recordedBlob === null || this.interactionMeta.isRecording) {
          return true;
        } else {
          return false;
        }
      }
    },
    isRecordAudioDisabled() {
      if (this.interactionMeta.isProcessingData) return true;
      return false;
    },
    showPlaceholderText() {
      if (this.hoverLabel) return true;
      return (
        this.input_length === 0
      );
    },
    record_upload_iframe_url() {
      if (
          this.queryParams.get('type') !== 'share'
      ) {
          let qs =  (this.user.userType === 'teacher') ? `&discussion_id=${this.interactionId}` : `&student_id=${this.user.id}&bulb_attempt_id=${this.bulbAttempt.id}`;
          return `${PlayerService.url}videotranscoder?token=${PlayerService.token}&teacher_id=${this.bulb.teachers[0].id}${qs}&active_color=${this.$vuetify.theme.primary.substring(1)}&base_color=${this.$vuetify.theme.secondary.substring(1)}&inactive_color=${this.$vuetify.theme.tertiary.substring(1)}`;
      } else {
          return '';
      }
    },
    tinymceEnvironment() {
      return tinymce.util.Tools.resolve('tinymce.Env');
    },
    showKeyboardShortcutIcon() {
      return ['discussion_q', 'free_response'].includes(this.interaction.interactionType);
    }
  },
  methods: {
    ...mapMutations('player', [
      'setSnackbar',
    ]),
    ...mapActions('player', [
      'lockSidebarStyle',
    ]),
    async deleteAttachment(attachment) {
      try {
        attachment.status = 'processing';
        await PlayerService.deleteAttachment({
          attachmentId: attachment.id,
          attachmentTypeId: _.isFinite(this.interaction.id) ? this.interaction.id : null,
          attachmentType: 'interactionAttempt',
        });
        this.$emit('update:attachments', this.attachments.filter((attached) => attached.id !== attachment.id));
      } catch(e) {
        attachment.status = 'ready';
        this.setSnackbar({
          text: `An error occurred while trying to delete "${attachment.name}". Please try again later.`,
          color: 'error',
        });
      }
    },
    downloadAttachment(attachment) {
      window.open(`${attachment.url}?bcdn_filename=${encodeURIComponent(attachment.name)}`);
    },
    async handleFileAttachment(e) {
      let attachment;
      let file;
      try {
        file = e.target.files[0];
        if (
          file.type.includes('audio/')
          || file.type.includes('image/')
          || file.type.includes('video/')
        ) {
          return this.setSnackbar({
            text: 'Audio, image, and video files are not viable attachments. These files can be inserted into the rich text editor through their own respective upload processes.',
            color: 'warning',
            timeout: 5000,
          });
        }
        if (file.size / Math.pow(1000, 2) > 25) {
          return this.setSnackbar({
            text: 'The maximum file size for attachments is 25MB.',
            color: 'warning',
            timeout: 5000,
          });
        }
        const key = this.$generateNumber('attachment-chips');
        this.$emit('update:attachments', [
          ...this.attachments,
          {
            status: 'processing',
            name: file.name,
            key,
          },
        ]);
        await this.$nextTick();
        attachment = this.attachments.find(attached => attached.key === key);
        const { data: fileLog } = await PlayerService.createAttachment({
          name: file.name,
          target: this.isFeedback ? 'feedback' : 'text',
          ..._.pick(file, ['name', 'size', 'type']),
          teacherId: this.user.id,
          ...this.user.schoolId && {
            schoolId: this.user.schoolId,
          }
        });
        const formData = new FormData();
        formData.append('file', file);
        const result = await axios({
          method: 'POST',
          url: fileLog.temporaryUploadURL,
          data: file,
          headers: {
            'Authorization': '',
            'Content-Type': 'application/octet-stream',
          }
        });
        for (const key in fileLog) {
          if (key !== 'status') attachment[key] = fileLog[key];
          else attachment[key] = 'ready';
        }
        await PlayerService.updateAttachment({
          id: attachment.id,
          status: 'ready',
        });
        return fileLog;
      } catch(e) {
        this.$emit('update:attachments', this.attachments.filter(attached => attached.key !== attachment.key));
        this.setSnackbar({
          text: `An error occurred while trying to upload "${file.name}". Please try again later.`,
          color: 'error',
          timeout: 5000,
        });
        if (!attachment?.id) return;
        await PlayerService.updateAttachment({
          id: attachment.id,
          status: 'error',
        });
      }
    },
    clear() {
      this.editor.setContent('');
      this.$emit('tinymce-input', '');
      this.input_length = 0;
    },
    async handleVideoTranscoderSuccess(data) {
      await this.$timer(1000);
      this.recordVideoMode = false;
      const str = `${this.inline ? 'inline' : 'block'}-textarea-${this.tinymceKey}`;
      const [vWidth, vHeight] = _.get(data, 'aspectRatio', '16:9').split(':');
      const aspectPadding = parseFloat(((parseInt(vHeight) / parseInt(vWidth)) * 100).toFixed(2));
      let url = '/';
      if (this.$inDevEnvironment()) {
        url = this.$getServerUrl();
        if (url === '/') url = `${window.location.origin}/`;
      }
      const html = `
        <div class="mux-video-wrapper" style="position: relative; height: 0; padding-bottom: ${aspectPadding}%">
          <iframe class="clickable" src="${url}hosted/video/?video_id=${data.playpositVideo.id}" width="100%" height="100%" frameBorder="0" allowfullscreen style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"></iframe>
        </div>
      `;
      tinymce.editors[str].execCommand('mceInsertContent', false, html);
      tinymce.editors[str].nodeChanged();
    },
    handleVideoTranscoderError(error) {
      dataLayer.errorLog(error)
    },
    handleVideoTranscoderExit(error_message) {
      this.recordVideoMode = false;
      if (error_message) {
        this.setSnackbar({ text: error_message, color: 'error' });
      }
    },
    setText(text){
      this.editor.setContent(text);
      this.$emit('tinymce-input', text);
      this.input_length = text.length;
    },
    resetMediaUploadField() {
      /**
       * Remove and then read upload image input field to clear out the old image.
       */
      this.mediaUpload = false;
      this.$nextTick(() => {
        this.mediaUpload = true;
      });
    },
    setTinymceText(text, key) {
      EventBus.$emit('setTinymce', {
        text,
        key
      });
    },
    handleAudioRecorderSuccess(audioUrl) {
      this.recordAudioMode = false;
      const id = `${this.inline ? 'inline' : 'block'}-textarea-${this.tinymceKey}`;
      tinymce.editors[id].execCommand('mceInsertContent', false, `<span class="playposit-audio-upload"><audio style="width: 100%" controls controlslist="nodownload"><source src="${audioUrl}" type="audio/wav" /></audio></span>`);
      tinymce.editors[id].nodeChanged();
    },
    handleAudioRecorderError(e) {
      this.setSnackbar({
        text: e.message,
        color: 'error'
      });
    },
  },
  watch: {
    'recordAudioMode'() {
      if (this.recordAudioMode) {
        this.$nextTick(() => {
          this.$refs.audioRecorder.scrollIntoView()
        })
      }
    },
  },
  beforeDestroy() {
    let tinymceId = (this.inline) ? `#inline-textarea-${this.tinymceKey}` : `#block-textarea-${this.tinymceKey}`;
    tinymce.remove(tinymceId);
  },
  async mounted() {
    this.rootTeacher = _.orderBy(this.bulb.teachers, 'bulbTeacher.bulbRole', 'asc')[0];
    this.serverURL = this.$getServerUrl();
    await this.$nextTick();
    if (this.existingText) this.input_length = this.existingText.length;
  },
};
</script>

<style>
  .tinymce-master-wrapper {
    position: relative;
    max-width: 100%;
  }

  .tinymce-master-wrapper.inline {
    /* margin-bottom: 15px; */
    min-height: 38px;
  }

  .tinymce-master-wrapper .fixed-toolbar-container {
    position: absolute;
    bottom: 100%;
    height: 69px;
    width: 100%;
    z-index: 10000;
    display: none;
  }

  .tinymce-master-wrapper .fixed-toolbar-container.toolbar-open {
    display: block;
  }

  .tinymce-master-wrapper .mce-tinymce {
    box-sizing: border-box;
  }

  .tinymce-master-wrapper .mce-tinymce:not(.mce-tinymce-inline) {
    min-height: 220px;
  }

  .tinymce-master-wrapper.inline .mce-tinymce-inline.mce-container .mce-menubar {
    display: none;
  }

  .tinymce-master-wrapper.inline .mce-tinymce-inline.mce-container.extended .mce-menubar {
    display: block;
  }

  .tinymce-master-wrapper .mce-tinymce-inline.mce-container {
    height: 50% !important;
    position: absolute;
    top: auto !important;
    bottom: 0;
    outline: 1px solid #9e9e9e;
    border: none;
  }

  .tinymce-master-wrapper .mce-tinymce-inline.mce-container.extended {
    height: 100% !important;
  }

  .tinymce-master-wrapper .button-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tinymce-master-wrapper .mce-container-body.mce-abs-layout {
    height: 100% !important;
    bottom: 0;
  }

  .tinymce-master-wrapper .mce-toolbar-grp.mce-last {
    top: auto !important;
    bottom: 0;
  }

  .tinymce-master-wrapper .alert.warning > div {
    white-space: normal;
  }

  i.mce-i-more_vert:before {
    content: "\e5d4";
    font-family: 'Material Icons';
    font-style: normal;
    font-weight: normal;
    text-decoration: inherit;
    color: #000;
    font-size: 1.5em;
    padding-right: 0.5em;
    position: absolute;
    top: 20%;
    left: 0;
  }



  .tinymce-master-wrapper .inline-textarea {
    padding: 4px 0;
    border-bottom: 1px solid transparent;
    overflow-y: auto;
    overflow-x: hidden;
    cursor: text;
    max-height: 130px;
    min-height: 30px;
    line-height: 1.5;
  }

  .tinymce-master-wrapper:not(.underline-only-on-focus) .inline-textarea.show-border {
    border-color: #9e9e9e;
  }

  .tinymce-master-wrapper.underline-only-on-focus.is-focused .inline-textarea.show-border {
    border-color: #9e9e9e;
  }

  .tinymce-master-wrapper.highlight-error .inline-textarea {
    border-color: #ff5252 !important;
  }

  .tinymce-master-wrapper .inline-textarea.mce-edit-focus {
    outline: 0;
    border-color: var(--branding-active-hex);
  }

  .tinymce-master-wrapper .inline-wrapper,
  .tinymce-master-wrapper .block-wrapper {
    position: relative;
  }

  .tinymce-master-wrapper .inline-wrapper.align-with-radio {
    margin-top: 6px;
  }

  .tinymce-master-wrapper .inline-wrapper.align-with-checkbox {
    margin-top: 3px;
  }

  .tinymce-master-wrapper .inline-wrapper.align-with-radio .placeholder {
    top: 5px;
  }

  .tinymce-master-wrapper .inline-wrapper .placeholder {
    top: 5px;
    position: absolute;
    pointer-events: none;
    font-size: 14px;
    transition: all .1s linear;
    z-index: 2;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .tinymce-master-wrapper .inline-wrapper .placeholder.hoverable {
    top: 3px;
    font-size: 16px;
  }

  .tinymce-master-wrapper .inline-wrapper .placeholder.is_hovered {
    top: -15px;
    font-size: 12px;
  }

  .tinymce-master-wrapper .block-wrapper .placeholder {
    position: absolute;
    top: 86px;
    left: 12px;
    pointer-events: none;
    font-size: 12px;
  }

  .items-partition {
    border-right: 1px solid rgb(204, 204, 204);
    width: 1px;
    height: 1.5rem;
  }

   .v-counter {
     margin-left: 7px;
   }
</style>
